// src/components/coins/GiveCoinsBar.js
//
// "🪙 Give coins" — for an educator, in the class Students popup. Gives to the
// whole class or to one student. The giveClassCoins Cloud Function checks the
// teacher owns the class code, that every student is on that roster, and the
// limits (up to 20 at a time, 50 per student per day from teachers).

import React, { useState } from "react";
import { giveClassCoins, useWallet } from "../../services/coinService";
import BuyCoinsButton from "./BuyCoinsButton";
import { Coin } from "./CoinCounter";
import "./coins.css";

const AMOUNTS = [5, 10, 20];
const BANK_AMOUNTS = [10, 25, 50, 100];
const REASONS = ["Great work!", "Super effort!", "Kind helper!", "Great listening!", "Awesome teamwork!"];

const GiveCoinsBar = ({ students = [], label }) => {
  const wallet = useWallet();
  const bank = wallet.classBank || 0;
  // "From class coin bank": coins the teacher bought — bigger amounts, no daily limit
  const [fromBank, setFromBank] = useState(false);
  const [amount, setAmount] = useState(5);
  const [reason, setReason] = useState(REASONS[0]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const uids = students.map((s) => s.uid || s.id).filter(Boolean);
  const who = label || (uids.length === 1 ? "this student" : `all ${uids.length} students`);

  const give = async () => {
    if (!uids.length) return;
    setBusy(true);
    setMsg(null);
    try {
      const out = await giveClassCoins({ studentUids: uids, amount, reason, fromBank });
      const parts = [`${out.given} student${out.given === 1 ? "" : "s"} got ${amount} coins`];
      if (fromBank && out.bankEmpty) parts.push("the class bank ran out — buy more coins to give the rest");
      if (fromBank) parts.push(`${out.classBank ?? 0} left in the bank`);
      if (!fromBank && out.capped?.length) {
        const n = out.capped.length;
        parts.push(`${n} already ${n === 1 ? "has" : "have"} today's 50 teacher coins`);
      }
      if (out.notInClass?.length) {
        const n = out.notInClass.length;
        parts.push(`${n} ${n === 1 ? "isn't" : "aren't"} on your class list`);
      }
      setMsg({ tone: out.given ? "ok" : "warn", text: `${parts.join(" · ")}.` });
    } catch (err) {
      setMsg({ tone: "bad", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sbl-give-coins">
      <Coin size={26} />
      <span className="sbl-give-title">Give coins</span>
      <span className="sbl-give-amounts" role="group" aria-label="How many coins">
        {(fromBank ? BANK_AMOUNTS : AMOUNTS).map((a) => (
          <button key={a} type="button" className={amount === a ? "is-on" : ""} onClick={() => setAmount(a)}>
            {a}
          </button>
        ))}
      </span>
      <select value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Why">
        {REASONS.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
      <label className="sbl-give-bank" title="Give coins you bought for your class (no daily limit)">
        <input
          type="checkbox"
          checked={fromBank}
          onChange={(e) => {
            setFromBank(e.target.checked);
            setAmount(e.target.checked ? 10 : 5);
          }}
        />{" "}
        From class bank ({bank.toLocaleString()})
      </label>
      {fromBank && bank < amount * uids.length && <BuyCoinsButton educator compact label="Add coins" />}
      <button type="button" className="sbl-give-go" onClick={give} disabled={busy || !uids.length || (fromBank && bank < amount)}>
        {busy ? "Giving…" : `Give to ${who}`}
      </button>
      {msg && <span className={`sbl-give-msg is-${msg.tone}`}>{msg.text}</span>}
    </div>
  );
};

export default GiveCoinsBar;
